import { IconButton, Spinner } from "@carbon6/c6-frontend-design";
import { useCallback, useMemo, type ReactNode } from "react";
import { GnbMenu } from "@/components/base";
import { createThumbnailRefreshDebouncer } from "@/components/dataset";
import { RefreshIcon } from "@/components/icons";
import { PageBase, type PageBaseProps } from "@/page/base/PageBase";
import { DataDashboardBody } from "./DataDashboardBody";
import { useDashboardGpuUsage } from "./useDashboardGpuUsage";
import { useDatasetAnalytics } from "./useDatasetAnalytics";
import { useRecentDatasets } from "./useRecentDatasets";
import { useTopDurationDatasets } from "./useTopDurationDatasets";

/**
 * 조직 전체 데이터셋의 통계 페이지.
 *
 * 단건 데이터셋 진단(상세 탭)과 분리된 개요 화면이다. 필터와 편집이 없는 read-only 화면이다.
 * 컨트롤은 수집 추이의 지표·버킷 전환뿐이다. 다른 화면으로 이동하는 경로는 최근 등록 카드의
 * 상세 창 열기뿐이다. 필터 드릴다운이 아니므로 개요 화면의 성격은 유지된다.
 *
 * KPI 행 아래에 GPU 사용량 요약 띠를 둔다. 띠의 기간은 고정이고, 이 페이지에는 기간 컨트롤이 없다.
 *
 * 조직 멤버면 조회가 200 이므로 등급 게이트를 두지 않는다. 통계는 팀이 다음에 무엇을 모을지
 * 정하는 입력이다. 그래서 특정 등급만 보는 화면이 아니다.
 */
export function DataDashboardPage() {
  const { state, bucket, setBucket, reload } = useDatasetAnalytics();
  const { state: recentState, reload: reloadRecent } = useRecentDatasets();
  const { state: topDurationState, reload: reloadTopDuration } = useTopDurationDatasets();
  const {
    usage: gpuUsage,
    cluster: gpuCluster,
    isLoading: isGpuLoading,
    reload: reloadGpu,
  } = useDashboardGpuUsage();
  // 목록 카드의 섬네일이 한꺼번에 만료되어도 재조회는 60초에 1회다. analytics 는 섬네일과
  // 관계없어 다시 받지 않는다. 두 목록 카드는 쿼리가 달라서 디바운서도 따로 둔다.
  const onThumbnailStale = useMemo(
    () => createThumbnailRefreshDebouncer(reloadRecent),
    [reloadRecent],
  );
  const onTopDurationThumbnailStale = useMemo(
    () => createThumbnailRefreshDebouncer(reloadTopDuration),
    [reloadTopDuration],
  );
  const reloadAll = useCallback(() => {
    reload();
    reloadRecent();
    reloadTopDuration();
    reloadGpu();
  }, [reload, reloadRecent, reloadTopDuration, reloadGpu]);
  return (
    <DataDashboardPageView
      pageTitle="Data Dashboard"
      currentMenu={GnbMenu.DataDashboard}
      scrollable
      state={state}
      bucket={bucket}
      onBucketChange={setBucket}
      onReload={reloadAll}
      recentState={recentState}
      onRecentRetry={reloadRecent}
      onThumbnailStale={onThumbnailStale}
      topDurationState={topDurationState}
      onTopDurationRetry={reloadTopDuration}
      onTopDurationThumbnailStale={onTopDurationThumbnailStale}
      gpuUsage={gpuUsage}
      gpuCluster={gpuCluster}
      isGpuLoading={isGpuLoading}
      onGpuRetry={reloadGpu}
    />
  );
}

type DataDashboardPageViewProps = PageBaseProps & {
  state: ReturnType<typeof useDatasetAnalytics>["state"];
  bucket: ReturnType<typeof useDatasetAnalytics>["bucket"];
  onBucketChange: (bucket: DataDashboardPageViewProps["bucket"]) => void;
  onReload: () => void;
  recentState: ReturnType<typeof useRecentDatasets>["state"];
  onRecentRetry: () => void;
  onThumbnailStale: () => void;
  topDurationState: ReturnType<typeof useTopDurationDatasets>["state"];
  onTopDurationRetry: () => void;
  onTopDurationThumbnailStale: () => void;
  gpuUsage: ReturnType<typeof useDashboardGpuUsage>["usage"];
  gpuCluster: ReturnType<typeof useDashboardGpuUsage>["cluster"];
  isGpuLoading: boolean;
  onGpuRetry: () => void;
};

class DataDashboardPageView extends PageBase<DataDashboardPageViewProps> {
  // 제목 오른쪽의 갱신 버튼. 이 페이지의 조회는 폴링하지 않는다. 그래서 이 버튼이 최신값을 받는
  // 유일한 방법이다. 통계, 목록 카드 둘, GPU 사용량을 함께 다시 받는다. GPU 띠의 `지금 점유` 만
  // 사이드바 위젯의 15초 스냅샷을 따른다.
  protected override renderHeaderActions(): ReactNode {
    return (
      <IconButton
        aria-label="통계 새로고침"
        variant="ghost"
        size="sm"
        disabled={
          this.props.state.status === "loading" ||
          this.props.recentState.status === "loading" ||
          this.props.topDurationState.status === "loading" ||
          this.props.isGpuLoading
        }
        onClick={this.props.onReload}
      >
        {this.props.isGpuLoading ? <Spinner size="sm" /> : <RefreshIcon />}
      </IconButton>
    );
  }

  protected override renderContent(): ReactNode {
    return (
      <DataDashboardBody
        state={this.props.state}
        bucket={this.props.bucket}
        onBucketChange={this.props.onBucketChange}
        onRetry={this.props.onReload}
        recentState={this.props.recentState}
        onRecentRetry={this.props.onRecentRetry}
        onThumbnailStale={this.props.onThumbnailStale}
        topDurationState={this.props.topDurationState}
        onTopDurationRetry={this.props.onTopDurationRetry}
        onTopDurationThumbnailStale={this.props.onTopDurationThumbnailStale}
        gpuUsage={this.props.gpuUsage}
        gpuCluster={this.props.gpuCluster}
        onGpuRetry={this.props.onGpuRetry}
      />
    );
  }
}
